import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {ButtonModule} from "primeng/button";
import {CommonModule} from "@angular/common";
import {UserService} from "../../services/user.service";
import {ActivatedRoute} from "@angular/router";
import {ApartmentService} from "../../services/apartment.service";

@Component({
  standalone: true,
  selector: 'app-exports-tab',
  imports: [
    CommonModule,
    ButtonModule,
  ],
  templateUrl: './exports-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportsTabComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly apartmentService = inject(ApartmentService);
  private readonly route = inject(ActivatedRoute);
  protected readonly userLoading = signal(false);
  protected readonly userMessage = signal<string | undefined>(undefined);
  protected readonly userError = signal<string | undefined>(undefined);
  protected readonly lotteryLoading = signal(false);
  protected readonly lotteryMessage = signal<string | undefined>(undefined);
  protected readonly lotteryError = signal<string | undefined>(undefined);
  protected readonly cancelLoading = signal(false);
  protected readonly cancelMessage = signal<string | undefined>(undefined);
  protected readonly cancelError = signal<string | undefined>(undefined);

  ngOnInit(): void {
    const csvKey = this.route.snapshot.queryParamMap.get('csvKey');
    if (csvKey) {
      this.startUserDownload(csvKey);
    }

    const lotteryCsvKey = this.route.snapshot.queryParamMap.get('lotteryCsvKey');
    if (lotteryCsvKey) {
      this.startLotteryDownload(lotteryCsvKey);
    }

    const cancelEventsCsvKey = this.route.snapshot.queryParamMap.get('cancelEventsCsvKey');
    if (cancelEventsCsvKey) {
      this.startCancelEventsDownload(cancelEventsCsvKey);
    }
  }

  protected requestUserExport() {
    this.userMessage.set(undefined);
    this.userError.set(undefined);
    this.userLoading.set(true);
    this.userService.requestCsvExport().subscribe({
      next: () => {
        this.userMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.userError.set('Failed to request export. Please try again.');
      },
      complete: () => this.userLoading.set(false),
    });
  }

  protected requestCancelEventsExport() {
    this.cancelMessage.set(undefined);
    this.cancelError.set(undefined);
    this.cancelLoading.set(true);
    this.userService.requestCancelEventsExport().subscribe({
      next: () => {
        this.cancelMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.cancelError.set('Failed to request export. Please try again.');
      },
      complete: () => this.cancelLoading.set(false),
    });
  }

  protected requestLotteryExport() {
    this.lotteryMessage.set(undefined);
    this.lotteryError.set(undefined);
    this.lotteryLoading.set(true);
    this.apartmentService.requestLotteryEligibilityExport().subscribe({
      next: () => {
        this.lotteryMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.lotteryError.set('Failed to request export. Please try again.');
      },
      complete: () => this.lotteryLoading.set(false),
    });
  }

  private startUserDownload(s3Key: string) {
    this.userMessage.set('Preparing download link...');
    this.userLoading.set(true);
    this.userService.getCsvDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'users.csv';
        anchor.click();
        this.userMessage.set('Download started.');
      },
      error: () => {
        this.userError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.userLoading.set(false),
    });
  }

  private startCancelEventsDownload(s3Key: string) {
    this.cancelMessage.set('Preparing download link...');
    this.cancelLoading.set(true);
    this.userService.getCancelEventsDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'cancel-events.csv';
        anchor.click();
        this.cancelMessage.set('Download started.');
      },
      error: () => {
        this.cancelError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.cancelLoading.set(false),
    });
  }

  private startLotteryDownload(s3Key: string) {
    this.lotteryMessage.set('Preparing download link...');
    this.lotteryLoading.set(true);
    this.apartmentService.getLotteryEligibilityDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'lottery-eligibility.csv';
        anchor.click();
        this.lotteryMessage.set('Download started.');
      },
      error: () => {
        this.lotteryError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.lotteryLoading.set(false),
    });
  }
}
